import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, Stack, Button, TextField, LinearProgress, ToggleButton, ToggleButtonGroup, styled, Chip } from "@mui/material";
import { CheckCircleOutline as ApproveIcon, CheckCircleOutline, CancelOutlined as RejectIcon, DescriptionOutlined as InfoIcon, BarChartOutlined as StatsIcon, AccessTime as WFHIcon, MoneyOff as LOPIcon } from '@mui/icons-material';
import { getData, putData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import { formatDate } from '../../../utils/dateFormat';
import { getUserRole } from '../../../utils/auth';

// Styled container for sections
const SectionPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: '16px',
    border: '1px solid #eef2f6',
    boxShadow: 'none',
    height: '100%'
}));

const statusStyles = {
    APPROVED: '#10b981',
    REJECTED: '#ef4444',
    PENDING: '#f59e0b',
}

function ViewLeaveRequest({ data, onClose, refresh }) {
    const [action, setAction] = useState('approve'); // 'approve' or 'reject'
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [employeeLeaveBalance, setEmployeeLeaveBalance] = useState([]);

    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const organizationId = userData?.organization_id;
    const userId = userData?.id;
    const userRole = getUserRole();

    const isProcessed = data?.status === "APPROVED" || data?.status === "REJECTED";

    const handleActionChange = (event, nextAction) => {
        if (nextAction !== null) setAction(nextAction);
    };

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                if (!data?.id || !organizationId || !userId) return;

                const employeeUrl = API_ENDPOINTS.getLeaveInfoByEmployeeId(data?.originalData?.user_id || data?.user_id);
                const orgUrl = API_ENDPOINTS.fetchLeaveCountsForOrganization(organizationId);
                const leaveHistoryUrls = API_ENDPOINTS.getEmployeeLeaveTypes(userId);

                const [empRes, orgRes] = await Promise.all([
                    getData(employeeUrl),
                    getData(orgUrl),
                    getData(leaveHistoryUrls)
                ]);
                if (empRes.status === "SUCCESS" && orgRes.status === "SUCCESS") {
                    const usageData = empRes.message.leaveTypesWithBalance || [];
                    const policyData = orgRes.message.leaveTypes || [];

                    // Merge usage data with policy data
                    const mergedData = usageData.map(usage => {
                        const policy = policyData.find(p => p.id === usage.leave_type_id);
                        const totalAllocated = policy?.max_days || 0;
                        const used = usage.used_days || 0;

                        return {
                            ...usage,
                            allocated_days: totalAllocated,
                            remaining: totalAllocated > 0 ? totalAllocated - used : 0
                        };
                    });

                    setEmployeeLeaveBalance(mergedData);
                }
            } catch (error) {
                return null;
            }
        };

        fetchAllData();
    }, [data?.id, organizationId, userId]); // Added userId to dependencies

    const handleSubmit = async () => {
        if (!data?.id) return;
        setSubmitting(true);
        try {
            const payload = {
                comment: comment,
                status: action === 'approve' ? 'APPROVED' : 'REJECTED'
            };

            const response = await putData(API_ENDPOINTS.updateLeaveRequestStatus(data.id), payload);

            if (response.status === "SUCCESS") {
                onClose();
                if (refresh) refresh();
            }
        } catch (error) {
            return null;
        } finally {
            setSubmitting(false);
        }
    };

    // Sync action state with incoming data status
    useEffect(() => {
        if (data?.status) {
            const normalizedStatus = data.status.toLowerCase();
            if (normalizedStatus === 'approved') setAction('approve');
            if (normalizedStatus === 'rejected') setAction('reject');
        }
    }, [data?.status]);

    return (
        <Box sx={{ p: 3, bgcolor: '#f8fafc' }}>
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 7 }}>
                    <Stack spacing={3}>
                        <SectionPaper>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                                <InfoIcon color="primary" />
                                <Typography variant="h6" fontWeight="700">Leave Request Details</Typography>
                            </Stack>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="textSecondary">Leave Type</Typography>
                                    <Typography variant="subtitle1" fontWeight="700">{data?.leaveType || data?.leave_type}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="textSecondary">Duration</Typography>
                                    <Typography variant="subtitle1" fontWeight="700">{data?.duration || data?.leave_duration || 0} day(s)</Typography>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="textSecondary">Start Date</Typography>
                                    <Typography variant="subtitle1" fontWeight="700">{formatDate(data?.startDate) || formatDate(data?.start_date) || '-'}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography variant="caption" color="textSecondary">End Date</Typography>
                                    <Typography variant="subtitle1" fontWeight="700">{formatDate(data?.endDate) || formatDate(data?.end_date) || '-'}</Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="caption" color="textSecondary">Reason</Typography>
                                    <Box sx={{ p: 2, mt: 1, bgcolor: '#f1f5f9', borderRadius: '12px', fontStyle: 'italic' }}>
                                        <Typography variant="body2" color="textPrimary">
                                            {data?.reason}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </SectionPaper>

                        <SectionPaper>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                                <CheckCircleOutline color="primary" sx={{ color: '#6366f1' }} />
                                <Typography variant="h6" fontWeight="700">Leave Balance Overview</Typography>
                            </Stack>

                            <Stack spacing={3}>
                                {employeeLeaveBalance
                                    .filter(item => !item.leave_type.includes("Work From Home") && !item.leave_type.includes("Loss of Pay"))
                                    .map((item) => {
                                        const total = item.allocated_days || 0;
                                        const used = item.used_days || 0;
                                        const rem = item.remaining || 0;
                                        const progress = total > 0 ? (used / total) * 100 : 0;

                                        return (
                                            <Box key={item.leave_type_id}>
                                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                                    <Typography variant="body2" fontWeight="600">{item.leave_type}</Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {rem} / {total} days remaining
                                                    </Typography>
                                                </Stack>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={progress}
                                                    sx={{
                                                        height: 8,
                                                        borderRadius: 5,
                                                        bgcolor: '#e2e8f0',
                                                        '& .MuiLinearProgress-bar': { bgcolor: '#6366f1' }
                                                    }}
                                                />
                                                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
                                                    Total: {total}  •  Used: {used}  •  <b>Rem: {rem}</b>
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                            </Stack>
                        </SectionPaper>
                    </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                    <Stack spacing={3}>
                        <SectionPaper>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                                <StatsIcon color="action" />
                                <Typography variant="subtitle1" fontWeight="700">Additional Statistics</Typography>
                            </Stack>

                            <Stack spacing={2}>
                                {employeeLeaveBalance
                                    .filter(item => item.leave_type.includes("Work From Home") || item.leave_type.includes("Loss of Pay"))
                                    .map((stat) => (
                                        <Box key={stat.leave_type_id} sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Box sx={{ color: '#94a3b8', display: 'flex' }}>
                                                    {stat.leave_type.includes("Work From Home") ? <WFHIcon /> : <LOPIcon />}
                                                </Box>
                                                <Typography variant="body2" color="textSecondary">{stat.leave_type}</Typography>
                                            </Stack>
                                            <Typography variant="body2" fontWeight="700">{stat.used_days} Days Used</Typography>
                                        </Box>
                                    ))}
                            </Stack>
                        </SectionPaper>

                        {userRole === 'Team Lead' ?
                            <Chip
                                label={data?.status}
                                size='medium'
                                sx={{ fontSize: 15,fontWeight: 700,
                                    backgroundColor: '#ffffffff',
                                    color: statusStyles[data?.status],
                                    border: `1px solid ${statusStyles[data?.status]}`
                                }} />
                            : <SectionPaper>
                                <ToggleButtonGroup
                                    value={action}
                                    exclusive
                                onChange={handleActionChange}
                                fullWidth
                                disabled={isProcessed}
                                sx={{
                                    mb: 3,
                                    flexDirection: {xs: 'column', sm: 'row'},
                                    bgcolor: '#f1f5f9',
                                    p: 0.5,
                                    borderRadius: '12px',
                                    border: 'none',
                                    '& .Mui-disabled.Mui-selected': {
                                        bgcolor: '#fff',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                        opacity: 1
                                    }
                                }}
                            >
                                <ToggleButton
                                    value="approve"
                                    sx={{
                                        borderRadius: '10px !important',
                                        border: 'none',
                                        '&.Mui-selected': { bgcolor: '#fff', color: '#10b981', fontWeight: 'bold' },
                                        '&.Mui-disabled.Mui-selected': { color: '#10b981' }
                                    }}
                                >
                                    <ApproveIcon sx={{ mr: 1 }} fontSize="small" />
                                    {data?.status === 'APPROVED' ? 'Approved' : 'Approve'}
                                </ToggleButton>

                                <ToggleButton
                                    value="reject"
                                    sx={{
                                        borderRadius: '10px !important',
                                        border: 'none',
                                        '&.Mui-selected': { bgcolor: '#fff', color: '#ef4444', fontWeight: 'bold' },
                                        '&.Mui-disabled.Mui-selected': { color: '#ef4444' }
                                    }}
                                >
                                    <RejectIcon sx={{ mr: 1 }} fontSize="small" />
                                    {data?.status === 'REJECTED' ? 'Rejected' : 'Reject'}
                                </ToggleButton>
                            </ToggleButtonGroup>

                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Add comments..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                disabled={isProcessed}
                                sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={handleSubmit}
                                disabled={submitting || isProcessed}
                                startIcon={action === 'approve' ? <ApproveIcon /> : <RejectIcon />}
                                sx={{
                                    borderRadius: '12px',
                                    py: 1.5,
                                    textTransform: 'uppercase',
                                    fontWeight: '700',
                                    bgcolor: action === 'approve' ? '#10b981' : '#ef4444',
                                    '&:hover': { bgcolor: action === 'approve' ? '#059669' : '#dc2626' }
                                }}
                            >
                                {submitting ? 'Processing...' : `${action} Leave Request`}
                            </Button>
                        </SectionPaper>}
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}

export default ViewLeaveRequest;